import { IconState } from '@/components';
import { IconName } from '@/components/icons/types';

import {
  BESSProjectTechnology,
  BiogasProjectTechnology,
  isBiogasProjectTechnology,
  isSolarProjectTechnology,
  ProjectStatus,
  ProjectTechnology,
  SolarProjectTechnology,
  TechnologyCollection,
  TechnologyType,
  WindProjectTechnology,
} from '../types';
import { customRound } from '@/helpers';

export function getTechnologyTypeFromCollection(collection: TechnologyCollection): TechnologyType {
  switch (collection) {
    case 'projects_bess_technologies':
      return 'bess';
    case 'projects_solar_technologies':
      return 'solar';
    case 'projects_wind_technologies':
      return 'wind';
    case 'projects_biogas_technologies':
      return 'biogas';
    case 'projects_hydrogen_technologies':
      return 'hydrogen';
    case 'projects_hidropower_technologies':
      return 'hidropower';
    case 'projects_datacenter_technologies':
      return 'datacenter';
    case 'projects_topco_technologies':
      return 'topco';
  }
}

export function getTechnologyTypesFromTechnologies(technologies: ProjectTechnology[]) {
  return technologies.map((technology) => getTechnologyTypeFromCollection(technology.collection));
}

export function getDevelopmentStatusFromTechnology(technologies: ProjectTechnology[]) {
  return technologies.map(({ item }) => item?.development_status);
}

export function getCapacityFromSolarTechnology(technology: SolarProjectTechnology) {
  return technology.installed_capacity_peak ?? 0;
}

export function getCapacityFromWindAndBESSTechnology(technology: WindProjectTechnology | BESSProjectTechnology) {
  return technology.installed_capacity ?? 0;
}

export function getCapacityFromBiogasTechnology(technology: BiogasProjectTechnology) {
  return technology.capacity ?? 0;
}

export function getCapacityFromTechnologies(technologies: ProjectTechnology[]) {
  return technologies
    .map(({ item }) => {
      if (isSolarProjectTechnology(item)) {
        return getCapacityFromSolarTechnology(item as SolarProjectTechnology);
      }
      if (isBiogasProjectTechnology(item)) {
        return getCapacityFromBiogasTechnology(item as BiogasProjectTechnology);
      }
      return getCapacityFromWindAndBESSTechnology(item as WindProjectTechnology | BESSProjectTechnology);
    })
    .reduce((prev, item) => customRound(prev ?? 0) + customRound(item ?? 0), 0);
}

export function getColorFromProjectStatus(status: ProjectStatus) {
  switch (status) {
    case 'draft':
      return 'disabled';
    case 'pending':
      return 'warning';
    case 'published':
      return 'info';
    case 'archived':
      return 'disabled';
  }
}

export function getIconByCollection(collection: TechnologyCollection): IconName {
  switch (collection) {
    case 'projects_bess_technologies':
      return 'bess';
    case 'projects_solar_technologies':
      return 'solar';
    case 'projects_wind_technologies':
      return 'wind';
    case 'projects_biogas_technologies':
      return 'biogas';
    case 'projects_hydrogen_technologies':
      return 'hydrogen';
    case 'projects_hidropower_technologies':
      return 'hidropower';
    case 'projects_datacenter_technologies':
      return 'datacenter';
    case 'projects_topco_technologies':
      return 'topco';
  }
}

export function getIconNamesFromTechnologies(technologies: ProjectTechnology[]) {
  return technologies.map(({ collection }) => getIconByCollection(collection));
}

export function getIconStatesFromTechnologies(technologies: ProjectTechnology[]) {
  return getIconNamesFromTechnologies(technologies).map((icon) => ({ icon, status: 'active' }) as IconState);
}

export function isDraft(status?: string) {
  return status === 'draft';
}

export function isPublished(status?: string) {
  return status === 'published';
}

export function generateLabel(title: string | null, baseLabel: string, country: string | null, suffix: string): string {

  if (title) {
    return `${baseLabel} (${title})`;
  }
  return country === 'ES' ? `${baseLabel} (${suffix})` : baseLabel;
}

export function isItemNull(item: any) {
  return Object.entries(item)
    .filter(([key]) => key !== 'id')
    .every(([, value]) => value === null);
}

export function extractTechCodes(technologies: any[] | null): string[] {
  return Array.isArray(technologies)
    ? technologies.map((t) => t.technologies_code)
    : [];
}

export function extractCountryCodes(markets: any[] | null): string[] {
  return Array.isArray(markets)
    ? markets.map((m) => m.countries_code)
    : [];
}

export function toTechJunction(codes: string[]) {
  return codes.map((code) => ({ technologies_code: code }));
}

export function toCountryJunction(codes: string[]) {
  return codes.map((code) => ({ countries_code: code }));
}
